import { TextField } from "@mui/material";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import axios from "axios";
import React from "react";
import "./App.css";

// Адрес API берём из рантайм-конфига (public/env.js), который на сервере
// генерируется из env.template.js подстановкой API_HOST.
// Если конфиг не подставился, возвращаем "" - относительный URL слать нельзя,
// иначе запрос уйдёт на сам reset-домен и nginx ответит 405.
// Пароль в системе - ровно 6 цифр (сервер валидирует тем же правилом).
const PASSWORD_LENGTH = 6;
const PASSWORD_PATTERN = /^[0-9]{6}$/;

const getApiDomain = () => {
  const domain = (window["env"] && window["env"]["apiDomain"]) || "";

  // Незаменённый плейсхолдер ${API_HOST} означает, что envsubst не отработал.
  if (domain === "" || domain.indexOf("${") !== -1) {
    return "";
  }

  return domain.replace(/\/+$/, "");
};

function App() {
  const [errors, setErrors] = React.useState({
    password: "",
    replyPassword: "",
  });

  const [message, setMessage] = React.useState(null);
  const [submitError, setSubmitError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const params = React.useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  const id = params.get("id") || "";
  const userId = params.get("userId") || "";
  const linkIsValid = id !== "" && userId !== "";

  const [values, setValues] = React.useState({
    password: "",
    replyPassword: "",
  });

  // Пароль - ровно 6 цифр: вырезаем всё, кроме цифр, и обрезаем до 6 символов.
  // Это отсекает и буквы, и вставку из буфера обмена.
  const handleChange = React.useCallback((field) => (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, PASSWORD_LENGTH);

    setValues((prev) => ({ ...prev, [field]: digits }));
    setErrors((prev) => (prev[field] === "" ? prev : { ...prev, [field]: "" }));
    setSubmitError("");
  }, []);

  let handleSubmit = (event) => {
    event.preventDefault();

    setMessage(null);
    setSubmitError("");

    const password = values.password;
    const replyPassword = values.replyPassword;

    let error = { password: "", replyPassword: "" };

    if (!PASSWORD_PATTERN.test(password)) {
      error.password = "Пароль должен состоять из цифр и быть длиной 6 символов";
    }

    if (password !== replyPassword) {
      error.replyPassword = "Пароли не совпадают";
    }

    setErrors(error);

    if (error.password !== "" || error.replyPassword !== "") {
      return;
    }

    if (!linkIsValid) {
      setSubmitError(
        "Ссылка недействительна. Запросите сброс пароля ещё раз."
      );
      return;
    }

    const apiDomain = getApiDomain();

    if (apiDomain === "") {
      setSubmitError(
        "Сервис временно недоступен: не задан адрес API. Сообщите администратору."
      );
      return;
    }

    setLoading(true);

    axios
      .post(`${apiDomain}/api/v1/auth/reset-password/confirm`, {
        id: id,
        userId: userId,
        password: password,
      })
      .then(
        () => {
          setMessage("Пароль изменён. Теперь войдите с новым паролем.");
          setDone(true);
        },
        (error) => {
          const status = error.response && error.response.status;
          const serverMessage =
            error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.error);

          if (status === 400) {
            setSubmitError(
              serverMessage ||
                "Ссылка недействительна или устарела. Запросите сброс пароля ещё раз."
            );
          } else if (status) {
            setSubmitError(
              serverMessage || "Не удалось изменить пароль. Попробуйте позже."
            );
          } else {
            setSubmitError(
              "Сервер недоступен. Проверьте соединение и попробуйте ещё раз."
            );
          }
        }
      )
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="App">
      <Card sx={{ maxWidth: "500px" }} className="Card">
        <form onSubmit={handleSubmit}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Смена пароля
            </Typography>

            {!linkIsValid ? (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                Ссылка недействительна. Запросите сброс пароля ещё раз.
              </Alert>
            ) : null}

            {message ? (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                {message}
              </Alert>
            ) : null}

            {submitError ? (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {submitError}
              </Alert>
            ) : null}

            {!done ? (
              <div className="Fields">
                <TextField
                  type="password"
                  autoComplete="new-password"
                  id="password"
                  label="Новый пароль"
                  variant="outlined"
                  name="password"
                  value={values.password}
                  disabled={!linkIsValid || loading}
                  onChange={handleChange("password")}
                  // maxLength здесь не годится: он режет строку ДО фильтрации,
                  // и при вставке "ab12cd34" остались бы только "12".
                  // Длину ограничивает сам handleChange.
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    width: "100%",
                  }}
                  error={errors.password !== ""}
                  helperText={
                    errors.password ||
                    `Ровно ${PASSWORD_LENGTH} цифр (${values.password.length}/${PASSWORD_LENGTH})`
                  }
                  margin="normal"
                />
                <TextField
                  type="password"
                  autoComplete="new-password"
                  id="replyPassword"
                  label="Повторите пароль"
                  variant="outlined"
                  name="replyPassword"
                  value={values.replyPassword}
                  disabled={!linkIsValid || loading}
                  onChange={handleChange("replyPassword")}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  sx={{
                    width: "100%",
                  }}
                  error={errors.replyPassword !== ""}
                  helperText={errors.replyPassword}
                  margin="normal"
                />
              </div>
            ) : null}
          </CardContent>
          {!done ? (
            <CardActions>
              <Button
                size="small"
                variant="contained"
                type="submit"
                disabled={!linkIsValid || loading}
                startIcon={
                  loading ? <CircularProgress color="inherit" size={16} /> : null
                }
              >
                {loading ? "Сохраняем..." : "Изменить пароль"}
              </Button>
            </CardActions>
          ) : null}
        </form>
      </Card>
    </div>
  );
}

export default App;
